import { cn } from '@/lib/utils';

const ALT_FULL = 'CIBA, Chartered Institute for Business Accountants NPC';

/**
 * The official CIBA logo (sources in brand-assets.json). Never redrawn, recoloured
 * or stretched; the files only have their empty margins trimmed and are scaled
 * proportionally.
 *
 * Dark surfaces use Marketing's white-and-lime logos. Light mode uses the
 * charcoal-and-lime logo (ciba-logo2.png): the wordmark alone in headers and
 * sidebars, with the institute's full name in the footer.
 *
 * - `wordmark` (headers, sidebars): "ciba" alone, white-and-lime on dark,
 *   charcoal-and-lime on light.
 * - `full` (footers, sign-in panels): the logo with the full name.
 *
 * `tone="on-dark"` is for surfaces that stay dark in both themes (the staff
 * sidebar, the sign-in panel). Clear space around the logo is at least the height
 * of its "c".
 */
export function LogoMark({
    className,
    suffix,
    variant = 'wordmark',
    tone = 'auto',
}: {
    className?: string;
    suffix?: string;
    variant?: 'wordmark' | 'full';
    tone?: 'auto' | 'on-dark';
}) {
    const light = tone === 'auto';

    if (variant === 'full') {
        return (
            <span className={cn('inline-flex', className)}>
                <img
                    src="/brand/ciba-full-name-white-lime.png"
                    alt={ALT_FULL}
                    width={114}
                    height={72}
                    className={cn('h-18 w-auto', light && '[.light_&]:hidden')}
                />
                {light && (
                    <img
                        src="/brand/ciba-full-name-charcoal-lime.png"
                        alt={ALT_FULL}
                        width={116}
                        height={72}
                        className="hidden h-18 w-auto [.light_&]:block"
                    />
                )}
            </span>
        );
    }

    return (
        <span className={cn('inline-flex items-center gap-[14px]', className)}>
            <img
                src="/brand/ciba-wordmark-white-lime.png"
                alt="CIBA"
                width={56}
                height={24}
                className={cn('h-6 w-auto', light && '[.light_&]:hidden')}
            />
            {light && (
                <img
                    src="/brand/ciba-wordmark-charcoal-lime.png"
                    alt="CIBA"
                    width={56}
                    height={24}
                    className="hidden h-6 w-auto [.light_&]:block"
                />
            )}
            {suffix && (
                // Sits on the wordmark's baseline.
                <span className="self-end stencil text-fg-secondary">{suffix}</span>
            )}
        </span>
    );
}
